import Box from "@material-ui/core/Box";
import TablePagination from "@material-ui/core/TablePagination";
import { Business, Group } from "@material-ui/icons";
import React, { useContext, useEffect, useRef, useState } from "react";
import UserActivityApi, { UserActivity } from "../../../api/services/UserActivityApi";
import UserInfoApi from "../../../api/services/UserInfoApi";
import CustomProgress from "../../../components/Alert/CustomProgress";
import CustomTable from "../../../components/CustomTable";
import LinearIndeterminate from "../../../components/Loading/LinearIndeterminate";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import GenericPermissionContext from "../../../contexts/GenericPermissionContext";
import { useLaboratories } from "../../../contexts/LaboratoriesContext";
import OrderedRequestHelper from "../../../helper/OrderedRequestHelper";
import { getRowsPerPage, setRowsPerPage } from "../../../helper/UserSettingsHelper";
import useEnv from "../../../hooks/env";
import Dictionary, { translate } from "../../../models/Dictionary";
import { UserActivityFilters } from "./AdminUserOverviewFilters";
import CSCLaboratory from "./CSCLaboratory";
import { UserAssignmentData, UserAssignmentType } from "./models/userAssignment";
import UserAssignmentDialog from "./UserAssignmentDialog";
import UserGroup, { userGroupDictionary, UserGroupReverseDictionary } from "./userGroups";
import { userRoleDictionary } from "./userRoles";
import getLaboratoryIds from "./utils";

type UserActivityTableRow = Record<Exclude<keyof UserActivity.SearchResult, "id">, string> & {
  id: number;
};

type ColumnName =
  | "Name"
  | "E-Mail"
  | "Laboratory"
  | "Managed Laboratory"
  | "Group"
  | "Last Login"
  | "Role(s) (at last login)";

type SortColumnName = "Name" | "E-Mail" | "Last Login";

const COLUMNS_ORDERED: ColumnName[] = [
  "Name",
  "E-Mail",
  "Laboratory",
  "Managed Laboratory",
  "Group",
  "Last Login",
  "Role(s) (at last login)",
];

const SORTABLE_COLUMNS = ["Name", "E-Mail", "Last Login"];

const sortColumnDictionary: { [SC in UserActivity.SortColumn]: SortColumnName } = {
  EMAIL_ADDRESS: "E-Mail",
  LAST_LOGIN: "Last Login",
  NAME: "Name",
};

const sortColumnReverseDictionary: { [K in SortColumnName]: UserActivity.SortColumn } = {
  "E-Mail": "EMAIL_ADDRESS",
  "Last Login": "LAST_LOGIN",
  Name: "NAME",
};

function isSortableColumn(columnName: ColumnName): columnName is SortColumnName {
  return SORTABLE_COLUMNS.includes(columnName);
}

function translateFromRowData<T extends string>(
  stringified: string,
  dictionary: Dictionary<string, T>,
  separator: string = ", ",
) {
  return stringified ? stringified.split(separator).map((entry) => dictionary[entry]) : [];
}

interface AdminUserOverviewTableProps {
  filters?: UserActivityFilters;
  terms?: UserActivity.SearchTerms;
  waiting: boolean;
}

const AdminUserOverviewTable: React.FC<AdminUserOverviewTableProps> = ({ filters, terms, waiting }) => {
  const [apiCalled, setApiCalled] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);
  const [orderedRequestHelper] = useState(new OrderedRequestHelper());
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(getRowsPerPage());
  const [sortColumn, setSortColumn] = useState<SortColumnName>(sortColumnDictionary.LAST_LOGIN);
  const [sortDirection, setSortDirection] = useState<UserActivity.SortDirection>("DESC");
  const [userAssignmentData, setUserAssignmentData] = useState<UserAssignmentData<UserAssignmentType> | null>(null);
  const [userId, setUserId] = useState<number | null>(null);
  const [userName, setUserName] = useState<string>("");
  const [userSearchResult, setUserSearchResult] = useState<UserActivity.SearchResultPagedResponse>({
    data: [],
    pageIndex,
    pageSize,
    totalData: 0,
    totalPages: 0,
  });
  const { api } = useEnv();
  const userActivityApi = new UserActivityApi(api);
  const userInfoApi = new UserInfoApi(api);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const { editUserGroups, editUserLaboratories, editLaboratoryManagers } = useContext(GenericPermissionContext);
  const {
    labs,
    labsLoading,
    labsDictionary: laboratoryDictionary,
    labsReverseDictionary: laboratoryReverseDictionary,
  } = useLaboratories();
  const doFetch = useRef(true);

  const fetch = () => {
    return orderedRequestHelper.ordered(() =>
      userActivityApi.fetchUserActivities({
        groupFilter: filters?.groups[0],
        labFilter: filters?.labs[0],
        pageIndex,
        pageSize,
        roleFilter: filters?.roles[0],
        sortColumn: sortColumnReverseDictionary[sortColumn],
        sortDirection,
        terms,
      }),
    );
  };

  const assignUserGroup = (id: number, groups: UserGroup[]) => {
    userInfoApi
      .changeUserGroups(id, groups)
      .then(() => showSnackbar("User groups updated", "success"))
      .then(() => {
        setIsLoading(true);
        return fetch();
      })
      .then(({ data }) => setUserSearchResult(data))
      .catch(handleHttpError)
      .finally(() => setIsLoading(false));
  };

  const assignUserLaboratory = (id: number, labs: CSCLaboratory[]) => {
    userInfoApi
      .changeUserLaboratories(id, labs)
      .then(() => showSnackbar("User laboratories updated", "success"))
      .then(() => {
        setIsLoading(true);
        return fetch();
      })
      .then(({ data }) => setUserSearchResult(data))
      .catch(handleHttpError)
      .finally(() => setIsLoading(false));
  };

  const assignManagedLaboratory = (id: number, labIds: number[]) => {
    userInfoApi
      .changeManagedLaboratories(id, { laboratoryIds: labIds })
      .then(() => showSnackbar("Managed laboratories updated", "success"))
      .then(() => {
        setIsLoading(true);
        return fetch();
      })
      .then(({ data }) => setUserSearchResult(data))
      .catch(handleHttpError)
      .finally(() => setIsLoading(false));
  };

  const generateRows = (searchResult: UserActivity.SearchResultPagedResponse): UserActivityTableRow[] => {
    return searchResult.data.map((row) => {
      return {
        id: row.id,
        name: row.name,
        emailAddress: row.emailAddress,
        laboratories: laboratoryDictionary ? translate(row.laboratories, laboratoryDictionary).join(", ") : "",
        managedLaboratories: laboratoryDictionary
          ? translate(
              row.managedLaboratories.map((l) => l.key),
              laboratoryDictionary,
            ).join(", ")
          : "",
        groups: translate(row.groups, userGroupDictionary).join(", "),
        lastLogin: row.lastLogin,
        roles: translate(row.roles, userRoleDictionary).join(", "),
      };
    });
  };

  const handleUserAssignment = (
    affectedUserId: number,
    affectedUserName: string,
    data: {
      userName?: string;
      data: CSCLaboratory[] | "APPROVER"[];
      type: UserAssignmentType;
    },
  ) => {
    setUserId(affectedUserId);
    setUserName(affectedUserName);
    setUserAssignmentData(data);
    setIsAssignmentDialogOpen(true);
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
    setPageIndex(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const rowsPerPage = parseInt(event.target.value, 10);
    setPageSize(rowsPerPage);
    setRowsPerPage(rowsPerPage);
    setPageIndex(0);
  };

  const handleDialogClose = () => {
    setIsAssignmentDialogOpen(false);
  };

  const handleDialogConfirm = (data: UserAssignmentData<UserAssignmentType>) => {
    if (!userId) {
      return;
    }
    switch (data.type) {
      case "GROUP":
        assignUserGroup(userId, data.data as UserGroup[]);
        break;
      case "LAB":
        assignUserLaboratory(userId, data.data as CSCLaboratory[]);
        break;
      case "MANAGED_LAB":
        assignManagedLaboratory(userId, getLaboratoryIds(labs, data.data as CSCLaboratory[]));
        break;
      default:
      // do nothing
    }
  };

  const updateSortColumn = (columnName: ColumnName) => {
    if (isSortableColumn(columnName)) {
      const newSortColumn = columnName;
      if (sortColumn === newSortColumn) {
        if (sortDirection === "ASC") {
          setSortDirection("DESC");
        } else {
          setSortDirection("ASC");
        }
      } else {
        setSortColumn(newSortColumn);
      }
    }
  };

  useEffect(() => {
    if (pageIndex > 0) {
      setPageIndex(0);
      doFetch.current = false;
    }
  }, [filters, terms]);

  useEffect(() => {
    if (doFetch.current) {
      setIsLoading(true);
      fetch()
        .then((res) => {
          setUserSearchResult(res.data);
          setIsLoading(false);
          setApiCalled(true);
        })
        .catch(handleHttpError);
    }
    return () => {
      doFetch.current = true;
    };
  }, [filters, pageIndex, pageSize, sortColumn, sortDirection, terms]);

  if (isLoading && !apiCalled) {
    return <CustomProgress />;
  }

  return (
    <>
      <LinearIndeterminate loading={isLoading || waiting || labsLoading || false} />
      {apiCalled && userSearchResult.data?.length === 0 && <Box marginTop={1}>No results found.</Box>}
      {userSearchResult?.data?.length > 0 && (
        <>
          <CustomTable
            columnExactWidths={["20%", "20%", "35%", "35%", "15%", "12.5%", "17.5%"]}
            columns={COLUMNS_ORDERED}
            excludedKeys={["id"]}
            hasFixedColumns
            headDense
            rows={generateRows(userSearchResult)}
            rowMenuActions={[
              {
                action: (rowData) =>
                  handleUserAssignment(rowData.id, rowData.name, {
                    data: translateFromRowData(rowData.groups, UserGroupReverseDictionary),
                    type: "GROUP",
                  }),
                icon: <Group />,
                show: () => editUserGroups,
                text: "Assign Groups",
              },
              {
                action: (rowData) => {
                  if (!labsLoading) {
                    handleUserAssignment(rowData.id, rowData.name, {
                      userName: rowData.name,
                      data: translateFromRowData(rowData.laboratories, laboratoryReverseDictionary || {}),
                      type: "LAB",
                    });
                  }
                },
                icon: <Business />,
                show: () => editUserLaboratories,
                text: "Assign Labs",
              },
              {
                action: (rowData) => {
                  if (!labsLoading) {
                    handleUserAssignment(rowData.id, rowData.name, {
                      userName: rowData.name,
                      data: translateFromRowData(rowData.managedLaboratories, laboratoryReverseDictionary || {}),
                      type: "MANAGED_LAB",
                    });
                  }
                },
                icon: <Business />,
                show: () => editLaboratoryManagers,
                text: "Assign Managed Labs",
              },
            ]}
            rowMenus
            sortableColumns={{ 0: true, 1: true, 4: true }}
            sortColumn={sortColumn}
            sortDirection={sortDirection.toLowerCase() as "asc" | "desc"}
            sortFunction={updateSortColumn}
          />
          <TablePagination
            component="div"
            count={userSearchResult.totalData}
            page={pageIndex}
            onChangePage={handleChangePage}
            rowsPerPage={pageSize}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
          {userAssignmentData && (
            <UserAssignmentDialog
              currentName={userName}
              currentValue={userAssignmentData}
              onConfirm={handleDialogConfirm}
              onClose={handleDialogClose}
              open={isAssignmentDialogOpen}
            />
          )}
        </>
      )}
    </>
  );
};

export default AdminUserOverviewTable;
